import { TrendingUp, TrendingDown, DollarSign, Percent } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {IndexFundHolding} from "@/integrations/supabase/indexfunds.models.ts";
import {computeIndexFundMetrics} from "@indexFundUtils";

export interface IndexFundDetailMetricsCardsProps {
  holding: IndexFundHolding;
  price: IndexFundPrice | null;
  isLoading: boolean;
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function MetricCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <Skeleton className="h-4 w-28" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-8 w-32" />
      </CardContent>
    </Card>
  );
}

export function IndexFundDetailMetricsCards({
  holding,
  price,
  isLoading,
}: IndexFundDetailMetricsCardsProps) {
  if (isLoading) {
    return (
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
        role="status"
        aria-label="Loading holding metrics"
        aria-busy="true"
        aria-live="polite"
      >
        <MetricCardSkeleton />
        <MetricCardSkeleton />
        <MetricCardSkeleton />
        <MetricCardSkeleton />
        <MetricCardSkeleton />
      </div>
    );
  }

  const currentNav = price?.price ?? 0;
  const currency = price?.currency ?? holding.currency;
  const { marketValue, costBasis, totalReturn, totalReturnPct } = computeIndexFundMetrics(
    holding,
    currentNav
  );

  const isPositiveReturn = totalReturn >= 0;
  const returnColorClass = isPositiveReturn ? 'text-emerald-600' : 'text-red-600';
  const ReturnIcon = isPositiveReturn ? TrendingUp : TrendingDown;

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
      role="region"
      aria-label="Index fund holding metrics"
    >
      {/* Current Market Value */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Market Value</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold" data-testid="market-value">
            {formatCurrency(marketValue, currency)}
          </div>
        </CardContent>
      </Card>

      {/* Total Cost Basis */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Cost Basis</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold" data-testid="cost-basis">
            {formatCurrency(costBasis, holding.currency)}
          </div>
        </CardContent>
      </Card>

      {/* Total Return ($) */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Total Return</CardTitle>
          <ReturnIcon className={`h-4 w-4 ${returnColorClass}`} aria-hidden="true" />
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${returnColorClass}`} data-testid="total-return">
            {formatCurrency(totalReturn, currency)}
          </div>
        </CardContent>
      </Card>

      {/* Total Return (%) */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Return %</CardTitle>
          <ReturnIcon className={`h-4 w-4 ${returnColorClass}`} aria-hidden="true" />
        </CardHeader>
        <CardContent>
          <div
            className={`text-2xl font-bold ${returnColorClass}`}
            data-testid="total-return-pct"
          >
            {totalReturnPct.toFixed(2)}%
          </div>
        </CardContent>
      </Card>

      {/* Expense Ratio */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Expense Ratio</CardTitle>
          <Percent className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold" data-testid="expense-ratio">
            {holding.expense_ratio != null ? `${holding.expense_ratio.toFixed(2)}%` : '—'}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
